import { AccountOverview, MarketDepth } from "@decibeltrade/sdk";

import { OrderSide } from "@/app/(main)/trade/order-form/order-form-state/store";

export const getNumDecimals = (num: number) => {
  // Use exponential notation to handle numbers with many decimals
  const expStr = Math.abs(num).toExponential();
  const [mantissa, exponent] = expStr.split("e");
  const exp = parseInt(exponent || "0", 10);

  // If exponent is non-negative, the number is >= 1
  if (exp >= 0) {
    // Count decimals in the mantissa part
    const mantissaParts = mantissa?.split(".") || [];
    if (mantissaParts.length === 1) {
      return 0;
    }
    const mantissaDecimals = mantissaParts[1]?.replace(/0+$/, "").length || 0;
    // Adjust for the exponent shift
    return Math.max(0, mantissaDecimals - exp);
  }

  // For numbers < 1, count leading zeros plus significant digits
  const mantissaParts = mantissa?.split(".") || [];
  const mantissaDecimals = mantissaParts[1]?.replace(/0+$/, "").length || 0;
  // Leading zeros = -exp - 1, plus the mantissa decimals
  return -exp + mantissaDecimals;
};

// Snaps float noise to a millionth so a value on a rounding boundary rounds as written.
const scaleSnapped = (num: number, decimals: number) =>
  Math.round(num * 10 ** decimals * 1e6) / 1e6;

/** Rounds down to `decimals` places; float noise at a boundary does not change the result. */
export function roundDownDecimals(num: number, decimals: number) {
  return Math.floor(scaleSnapped(num, decimals)) / 10 ** decimals;
}

/** Rounds up to `decimals` places; float noise at a boundary does not change the result. */
export function roundUpDecimals(num: number, decimals: number) {
  return Math.ceil(scaleSnapped(num, decimals)) / 10 ** decimals;
}

export function roundDecimals(num: number, decimals: number) {
  return Math.round(num * 10 ** decimals) / 10 ** decimals;
}

export function roundDecimalsWithMinDigits(num: number, minDigits: number) {
  const intDigits = Math.floor(num).toString().length;
  const decimals = Math.max(0, minDigits - intDigits);
  return roundDecimals(num, decimals);
}

/**
 * Converts a decimal amount to chain units (e.g., USDC).
 * For USDC, this means multiplying by 10^6.
 * Note: USDC will be the default token for the time being.
 * @param amount
 * @param decimal - The number of decimal places for the token (default is 6 for USDC).
 * @returns
 */
export function amountToChainUnits(amount: number, decimal = 6): number {
  return Math.floor(amount * 10 ** decimal + 1e-6); // add a small epsilon to avoid floating point precision issues
}

/**
 * Converts a display price to tick-aligned chain units in a single step,
 * avoiding the float round-trip of roundToValidPrice → amountToChainUnits.
 *
 * Uses Math.round to snap away float drift (~1e-10), which is safe for any
 * realistic price. TODO: migrate to a decimal library if we ever need
 * precision beyond JS float-safe integers (2^53).
 */
export function priceToChainUnits(
  displayPrice: number,
  pxDecimals: number,
  tickSize: number,
): number {
  if (displayPrice === 0 || tickSize === 0) return 0;
  const raw = Math.round(displayPrice * 10 ** pxDecimals);
  return Math.round(raw / tickSize) * tickSize;
}

/**
 * Converts chain units to decimal amount (e.g., USDC).
 * For USDC, this means dividing by 10^6.
 * Note: USDC will be the default token for the time being.
 * @param chainUnits
 * @param decimal - The number of decimal places for the token (default is 6 for USDC).
 * @returns
 */
export function chainUnitsToAmount(chainUnits: number, decimal = 6): number {
  return chainUnits / 10 ** decimal;
}

/**
 * Calculates the unrealized PnL for a position.
 * Formula: position size * (current price - entry price)
 * @param size - Position size (positive for long, negative for short)
 * @param entryPrice - Entry price of the position
 * @param currentPrice - Current market price
 * @returns The calculated unrealized PnL and percentage
 */
export function calculateUnrealizedPnl(size: number, entryPrice: number, currentPrice: number) {
  if (size === 0) return { pnl: 0, percentage: 0 };

  const pnl = size * (currentPrice - entryPrice);

  const percentage = pnl / (Math.abs(size) * entryPrice);

  return { pnl, percentage };
}

/**
 * Trade-history rows carry no entry price, but realized PnL pins it exactly:
 * a long realizes `(exit - entry) * size`, a short the inverse.
 *
 * `realizedPnl` must be the gross on-chain amount. Netting the fee out first
 * would push the derived entry price away from the real one by the fee.
 */
export function deriveClosedPositionEntryPrice({
  exitPrice,
  realizedPnl,
  size,
  isLong,
}: {
  exitPrice: number;
  realizedPnl: number;
  /** Closed quantity, always positive — the API sends side via `action`. */
  size: number;
  isLong: boolean;
}): number | undefined {
  if (!(size > 0) || !Number.isFinite(exitPrice) || !Number.isFinite(realizedPnl)) {
    return undefined;
  }

  const perUnitPnl = realizedPnl / size;
  const entryPrice = isLong ? exitPrice - perUnitPnl : exitPrice + perUnitPnl;

  // A non-positive entry is impossible on-chain, so the inputs disagree.
  return entryPrice > 0 ? entryPrice : undefined;
}

/**
 * The PnL a closed-position card shows, kept identical to the `pnlAtClose`
 * table cell the card is opened from — the two sit side by side, so any
 * disagreement reads as a bug.
 *
 * Perp fees are USDC, so PnL can be shown fee-inclusive. Spot fees are
 * denominated in the fee asset (base for buys) and aren't subtractable from a
 * USD PnL, so spot shows the realized PnL alone.
 */
export function getClosedPositionDisplayPnl({
  realizedPnl,
  fee,
  isSpot,
}: {
  realizedPnl: number;
  fee: number;
  isSpot: boolean;
}): number {
  return isSpot ? realizedPnl : realizedPnl - fee;
}

/**
 * Calculates the margin for a position.
 * Formula: |position size| * entry price / user leverage
 * @param size - Position size (can be positive for long or negative for short)
 * @param entryPrice - Entry price of the position
 * @param userLeverage - User leverage for the position
 * @returns The calculated margin
 */
export function calculateMargin(size: number, price: number, userLeverage: number): number {
  return (Math.abs(size) * price) / userLeverage;
}

/**
 * Calculates the slippage for an order.
 * @param marketDepth - The market depth
 * @param orderSide - The side of the order
 * @param orderSize - The size of the order
 * @returns The slippage for the order (null if there is insufficient liquidity), and the total cost of the order
 */
export function calculateSlippage(
  marketDepth: MarketDepth,
  orderSide: OrderSide,
  orderSize: number,
): { slippage: number | null; totalCost: number | null } {
  if (orderSize === 0) return { slippage: 0, totalCost: 0 };

  // Asks are sorted ascending (best/lowest first), bids descending (best/highest first)
  const bestAsk = marketDepth.best_ask ?? marketDepth.asks[0]?.price;
  const bestBid = marketDepth.best_bid ?? marketDepth.bids[0]?.price;
  const midPrice =
    bestAsk === undefined || bestBid === undefined
      ? (bestAsk ?? bestBid ?? 0)
      : (bestAsk + bestBid) / 2;

  if (midPrice === 0) return { slippage: null, totalCost: null };

  const levels = orderSide === "buyLong" ? marketDepth.asks : marketDepth.bids;

  let remaining = orderSize;
  let totalCost = 0;
  let totalFilled = 0;

  for (const level of levels) {
    const fillSize = Math.min(remaining, level.size);
    totalCost += fillSize * level.price;
    totalFilled += fillSize;
    remaining -= fillSize;
    if (remaining <= 0) break;
  }

  if (remaining > 0) return { slippage: null, totalCost: null };

  // Volume Weighted Average Price
  const vwap = totalCost / totalFilled;

  const slippage = Math.abs(vwap - midPrice) / midPrice;

  return { slippage, totalCost };
}

export function toPrecision(num: number, precision: number) {
  if (num === 0) return 0;

  const stringNum = num.toString();

  if (!stringNum.includes(".")) return num;

  const [integerPart] = stringNum.split(".");

  if (integerPart && integerPart.length >= precision) return Number(integerPart);

  const toFixed = num < 1 ? precision : precision - (integerPart?.length ?? 0);

  return Number(num.toFixed(toFixed));
}

/**
 * Perp-only PnL. Kept separate from `getCombinedPnl` because `utils/vault.ts`
 * reuses it for a vault's `all_time_earned`, and vaults are a perp construct.
 */
export const getTotalPnl = (accountOverview: AccountOverview | undefined) => {
  if (!accountOverview) return 0;
  return (
    accountOverview.unrealized_pnl +
    (accountOverview.realized_pnl ?? 0) -
    accountOverview.unrealized_funding_cost +
    (accountOverview.liquidation_losses ?? 0) +
    (accountOverview.fee_income ?? 0)
  );
};

/**
 * Spot PnL: unrealized on current inventory, plus lifetime realized NET OF
 * FEES. Both legs are lifetime, matching `getTotalPnl` (perp `realized_pnl` is
 * lifetime too), so the two compose without mixing windows.
 *
 * Fees are subtracted here because `cumulative_realized_pnl_usd` is gross while
 * the perp leg of the same tile already nets them out. Leaving spot gross makes
 * a flat round trip read as zero when the account actually paid the spread.
 * The standalone "Spot Realized PnL" row stays gross — it renders beside its
 * own "Spot Fees Paid" row.
 *
 * `total_unrealized_pnl_usd` covers PFS holdings AND base escrowed behind
 * resting orders; the `positions[]` fallback exists only for servers predating
 * it and misses escrow. Assets with no on-book cost basis contribute 0.
 */
export const getSpotPnl = (accountOverview: AccountOverview | undefined) => {
  const spot = accountOverview?.spot;
  if (!spot) return 0;
  const unrealized =
    spot.total_unrealized_pnl_usd ??
    spot.positions.reduce((acc, position) => acc + position.unrealized_pnl_usd, 0);
  const metrics = spot.metrics;
  if (!metrics) return unrealized;
  return (
    unrealized +
    metrics.cumulative_realized_pnl_usd -
    metrics.cumulative_taker_fees_usd -
    metrics.cumulative_maker_fees_usd
  );
};

/** Portfolio-wide PnL across both engines. Pairs with the Portfolio Value tile. */
export const getCombinedPnl = (accountOverview: AccountOverview | undefined) =>
  getTotalPnl(accountOverview) + getSpotPnl(accountOverview);

export const getAverageCashPosition = (accountOverview: AccountOverview | undefined) => {
  if (!accountOverview?.perp_equity_balance) return 0;
  return accountOverview.usdc_cross_withdrawable_balance / accountOverview.perp_equity_balance;
};

/**
 * Leverage is gross exposure over equity, so each position contributes its
 * absolute notional: `currentValue` is signed (size * markPx), and summing it
 * raw makes a short read as negative leverage and a hedged book read as none.
 * Returns undefined for a zero-equity account, which would otherwise be NaN.
 */
export const getAverageLeverage = (
  positions: Array<{ currentValue: number }>,
  perpEquityBalance: number,
) => {
  if (perpEquityBalance <= 0) return undefined;
  const grossNotional = positions.reduce(
    (acc, position) => acc + Math.abs(position.currentValue),
    0,
  );
  return grossNotional / perpEquityBalance;
};
